import React, { ChangeEvent, Component } from "react";
import { observable } from "mobx";

import { IDeviceConfigurationDocument } from "@formant/core/model/IDeviceConfigurationDocument";
import { IDeviceTeleopHardwareStreamConfiguration } from "@formant/core/model/IDeviceTeleopHardwareStreamConfiguration";
import { IValidation } from "@formant/core/model/IValidation";
import { videoQualities } from "@formant/core/model/videoQualities";
import { VideoQuality } from "@formant/core/model/VideoQuality";
import { InputElement } from "@formant/ui/component/common/Field";
import { HStack } from "@formant/ui/component/common/HStack";
import { TextField } from "@formant/ui/component/common/TextField";

import { Help } from "../common/Help";
import { NumberField } from "../common/NumberField";
import { SelectField } from "../common/SelectField";
import { Switch } from "../common/Switch";
import {
    EditTeleopStreamPopup,
    IEditTeleopStreamPopupProps,
} from "./EditTeleopStreamPopup";

export interface ITeleopHardwareVideoConfigurationProps extends IEditTeleopStreamPopupProps {
    streamName?: string;
    document: IDeviceConfigurationDocument;
    realtimeStreamNames?: string[];
    realtimeMode?: boolean;
    onChange: (document: IDeviceConfigurationDocument) => void;
    onClose: () => void;
}

interface IErrorState {
    errorMessage: string;
}

export class TeleopHardwareVideoConfiguration extends Component<ITeleopHardwareVideoConfigurationProps> {
    @observable
    private validation: IValidation | undefined;

    private label?: string;
    private initialLabel?: string;

    // eslint-disable-next-line react/state-in-constructor
    state: IErrorState = {
        errorMessage: "",
    };

    public render() {
        const { streamName, document, realtimeMode, visible } = this.props;
        const { errorMessage } = this.state;
        const stream = (
            realtimeMode ? document.realtime : document.teleop
        )?.hardwareStreams?.find(_ => _.name === streamName);
        if (stream?.label) {
            this.label = stream.label;
            if (!this.initialLabel) {
                this.initialLabel = stream.label;
            }
        }
        const type = stream?.hardwareType;
        return (
            <EditTeleopStreamPopup
                title={`Configuration for: ${streamName}`}
                onClose={this.handleClose}
                visible={visible}
            >
                {realtimeMode && (
                    <>
                        <TextField
                            value={stream?.label || ""}
                            onChange={this.onChangeLabel(stream)}
                            label="Connection"
                            placeholder="Connection"
                            autoFocus={true}
                            small={true}
                        />
                        {errorMessage && (
                            <div style={{ color: "red" }}>{errorMessage}</div>
                        )}
                    </>
                )}
                {type === "usb" && (
                    <>
                        <HStack vAlign="center" hAlign="space-between">
                            <div>Video quality</div>
                            <Help
                                text="Preferred video quality. If this video source is also configured for telemetry, the lower of the two qualities will be used."
                                to="https://docs.formant.io/docs/getting-started-add-a-teleoperation-image-stream"
                            />
                        </HStack>
                        <SelectField
                            label="Preferred video quality"
                            value={stream?.quality}
                            onChange={this.onChangeQuality(stream)}
                        >
                            {videoQualities.map(_ => (
                                <option key={_} value={_}>
                                    {_}
                                </option>
                            ))}
                        </SelectField>
                    </>
                )}
                {type === "ip" && (
                    <Switch
                        state={stream?.rtspEncodingNeeded ?? false}
                        onChange={this.onChangeRTSPEncodingNeeded(stream)}
                        label="Encoding required?"
                        stateLabels={["No", "Yes"]}
                        info="If the IP camera source outputs H264, set this to NO."
                    />
                )}
                <HStack vAlign="center" hAlign="space-between">
                    <div>Advanced Settings</div>
                    <Help
                        text="If you also configure your video settings via environment variables, the environment variables will override what you configure here. Click for docs."
                        to="https://docs.formant.io/docs/getting-started-add-a-teleoperation-image-stream"
                    />
                </HStack>
                <HStack vAlign="center" hAlign="start" innerSpacing="sm">
                    <NumberField
                        label="Bitrate"
                        numberValue={stream?.bitrate}
                        allowUndefined={true}
                        invalid={!!this.validation?.bitrate}
                        onChangeNumber={this.onChangeBitrate(stream)}
                    />
                    <Help
                        text="Set your desired bitrate in kbps. If left blank, the agent will choose the best bitrate for the preferred quality selected above. If specified, the agent will be forced to use this bitrate. Click for docs."
                        to="https://docs.formant.io/docs/getting-started-add-a-teleoperation-image-stream"
                    />
                </HStack>
                <Switch
                    state={stream?.disableAdaptiveQuality ?? false}
                    onChange={this.onToggleDisableAdaptiveQuality(stream)}
                    label="Disable Adaptive Quality"
                    stateLabels={["No", "Yes"]}
                    info="If set to No, the Formant agent will be able to reduce video quality in poor network connection to maintain video stream. If set to Yes, the agent will not be allowed to change the video quality."
                />
                <Switch
                    state={stream?.overlayClock ?? false}
                    onChange={this.onToggleOverlayClock(stream)}
                    label="Clock Overlay"
                    stateLabels={["No", "Yes"]}
                    info="Overlay the video stream with a clock."
                />
            </EditTeleopStreamPopup>
        );
    }
    private handleClose = () => {
        const { onClose } = this.props;

        if (this.state.errorMessage !== "") {
            return;
        }

        this.initialLabel = undefined;
        onClose();
    };
    private onChangeLabel =
        (stream: IDeviceTeleopHardwareStreamConfiguration | undefined) =>
        (event: ChangeEvent<InputElement>) => {
            if (!stream) {
                return;
            }
            const { document, onChange, realtimeMode } = this.props;
            this.label = event.target.value;
            stream.label = event.target.value;

            if (!this.label && realtimeMode) {
                this.setState({ errorMessage: "Name cannot be empty" });
                return;
            }

            if (
                this.label &&
                realtimeMode &&
                this.props.realtimeStreamNames?.includes(this.label) &&
                this.label !== this.initialLabel
            ) {
                this.setState({ errorMessage: "Name already in use" });
                return;
            }

            this.setState({ errorMessage: "" });

            onChange({ ...document });
        };
    private onChangeQuality =
        (stream: IDeviceTeleopHardwareStreamConfiguration | undefined) =>
        (event: ChangeEvent<HTMLSelectElement>) => {
            if (!stream) {
                return;
            }
            const { document, onChange } = this.props;
            stream.quality = event.target.value as VideoQuality;
            onChange({ ...document });
        };

    private onChangeRTSPEncodingNeeded =
        (stream: IDeviceTeleopHardwareStreamConfiguration | undefined) =>
        (newValue: boolean) => {
            if (!stream) {
                return;
            }
            const { document, onChange } = this.props;
            stream.rtspEncodingNeeded = newValue;
            onChange({ ...document });
        };

    private onChangeBitrate =
        (stream: IDeviceTeleopHardwareStreamConfiguration | undefined) =>
        (newValue: number | undefined) => {
            if (!stream) {
                return;
            }

            if (newValue !== undefined && newValue < 1) {
                this.validation = {
                    bitrate: ["Bitrate must be positive"],
                };
                return;
            } else {
                this.validation = undefined;
            }
            const { document, onChange } = this.props;
            stream.bitrate = newValue;
            onChange({ ...document });
        };

    private onToggleDisableAdaptiveQuality =
        (stream: IDeviceTeleopHardwareStreamConfiguration | undefined) =>
        (newValue: boolean) => {
            if (!stream) {
                return;
            }
            const { document, onChange } = this.props;
            stream.disableAdaptiveQuality = newValue;
            onChange({ ...document });
        };

    private onToggleOverlayClock =
        (stream: IDeviceTeleopHardwareStreamConfiguration | undefined) =>
        (newValue: boolean) => {
            if (!stream) {
                return;
            }
            const { document, onChange } = this.props;
            stream.overlayClock = newValue;
            onChange({ ...document });
        };
}
